// geoBoundaries API クライアント。ブラウザからメタデータと行政界 GeoJSON を直接取得する。
const API_BASE = 'https://www.geoboundaries.org/api/current'
export const MAX_DOWNLOAD_BYTES = 50 * 1024 * 1024

const RELEASE_TYPES = new Set(['gbOpen', 'gbHumanitarian', 'gbAuthoritative'])
const ADM_LEVELS = new Set(['ADM0', 'ADM1', 'ADM2', 'ADM3', 'ADM4', 'ADM5', 'ALL'])

function summarizeMetadata(entry) {
  return {
    boundaryISO: entry.boundaryISO,
    boundaryName: entry.boundaryName,
    boundaryType: entry.boundaryType,
    boundaryYearRepresented: entry.boundaryYearRepresented,
    admUnitCount: Number(entry.admUnitCount) || undefined,
    meanVertices: Number(entry.meanVertices) || undefined,
    boundaryLicense: entry.boundaryLicense,
    boundarySource: entry.boundarySource,
    licenseSource: entry.licenseSource,
    gjDownloadURL: entry.gjDownloadURL,
    simplifiedGeometryGeoJSON: entry.simplifiedGeometryGeoJSON,
  }
}

export async function getBoundaryMetadata({
  iso3,
  admLevel = 'ALL',
  releaseType = 'gbOpen',
  fetchImpl = globalThis.fetch,
  signal,
}) {
  const iso = String(iso3 ?? '').toUpperCase()
  const adm = String(admLevel ?? 'ALL').toUpperCase()

  if (!/^[A-Z]{3}$/.test(iso) && iso !== 'ALL') {
    throw new Error(`iso3 は3文字の国コード（例: JPN, USA）で指定してください: ${iso3}`)
  }
  if (!ADM_LEVELS.has(adm)) throw new Error(`未対応の行政界レベルです: ${admLevel}`)
  if (!RELEASE_TYPES.has(releaseType)) throw new Error(`未対応のリリース種別です: ${releaseType}`)

  const response = await fetchImpl(`${API_BASE}/${releaseType}/${iso}/${adm}/`, { signal })
  if (response.status === 404) {
    throw new Error(`geoBoundaries に該当データがありません（${iso} ${adm}）`)
  }
  if (!response.ok) {
    throw new Error(`geoBoundaries API の呼び出しに失敗しました（HTTP ${response.status}）`)
  }
  const payload = await response.json()
  const entries = Array.isArray(payload) ? payload : [payload]
  if (!entries.length || !entries[0]?.boundaryISO) {
    throw new Error(`geoBoundaries に該当データがありません（${iso} ${adm}）`)
  }
  return entries.map(summarizeMetadata)
}

export function toCorsFriendlyUrl(url) {
  const match = String(url).match(/^https:\/\/github\.com\/([^/]+)\/([^/]+)\/raw\/(.+)$/)
  if (!match) return url
  return `https://media.githubusercontent.com/media/${match[1]}/${match[2]}/${match[3]}`
}

export async function downloadBoundaryGeoJSON(
  url,
  { fetchImpl = globalThis.fetch, signal, maxBytes = MAX_DOWNLOAD_BYTES } = {},
) {
  const response = await fetchImpl(toCorsFriendlyUrl(url), { signal })
  if (!response.ok) throw new Error(`行政界データの取得に失敗しました（HTTP ${response.status}）`)
  const contentLength = Number(response.headers?.get?.('content-length'))
  if (Number.isFinite(contentLength) && contentLength > maxBytes) {
    throw new Error('行政界データが50MBの上限を超えています。simplifiedを使用してください。')
  }
  const text = await response.text()
  if (new Blob([text]).size > maxBytes) {
    throw new Error('行政界データが50MBの上限を超えています。simplifiedを使用してください。')
  }
  const geojson = JSON.parse(text)
  if (geojson?.type !== 'FeatureCollection') throw new Error('取得結果がFeatureCollectionではありません')
  return geojson
}
